/**
 * Coach Ego Network Dashboard Configuration
 *
 * Zone colors, layout parameters, thresholds, and constants
 * for the ego network visualization.
 */

// Zone definitions for concentric ring layout
export const ZONES = {
  INNER: 'inner',
  COMMUNITY: 'community',
  NETWORK: 'network',
};

// Zone display config (colors match HTML prototype)
export const ZONE_CONFIG = {
  [ZONES.INNER]: {
    label: 'Inner Circle',
    color: '#23A38F',        // teal
    gradient: ['#23A38F', '#1a8a78'],
    ringBorderColor: 'rgba(16, 3, 49, 0.18)',
    description: 'Closest relationships with highest interaction',
  },
  [ZONES.COMMUNITY]: {
    label: 'Community',
    color: '#0097B2',        // blue
    gradient: ['#0097B2', '#007a91'],
    ringBorderColor: 'rgba(16, 3, 49, 0.12)',
    description: 'Regular collaborators and team members',
  },
  [ZONES.NETWORK]: {
    label: 'Network',
    color: '#D8A200',        // gold
    gradient: ['#D8A200', '#b88e00'],
    ringBorderColor: 'rgba(16, 3, 49, 0.08)',
    description: 'Broader professional connections',
  },
};

// Ego node config
export const EGO_CONFIG = {
  gradient: ['#100331', '#2a1060'],
  size: 72,
  glowColor: 'rgba(35, 163, 143, 0.3)',
};

// Layout parameters for concentric mode
export const CONCENTRIC_LAYOUT = {
  ringRadii: {
    [ZONES.INNER]: 120,
    [ZONES.COMMUNITY]: 230,
    [ZONES.NETWORK]: 340,
  },
  nodeSize: {
    ego: 72,
    default: 52,
    small: 44,
  },
  fontSize: {
    ego: 20,
    default: 16,
    name: 11,
    role: 9,
  },
};

// Force layout parameters
export const FORCE_LAYOUT = {
  chargeStrength: -400,
  linkDistance: 120,
  collisionRadius: 40,
  centerStrength: 0.05,
  alphaDecay: 0.02,
};

// Edge thickness range (maps evidence count to pixel width)
export const EDGE_THICKNESS = {
  min: 1,
  max: 4,
  defaultStrength: 'medium', // weak | medium | strong
};

// Connection strength thresholds (evidence_count based)
export const STRENGTH_THRESHOLDS = {
  strong: 10,    // 10+ co-mentions
  medium: 4,     // 4-9 co-mentions
  weak: 0,       // 1-3 co-mentions
};

// Zone assignment proxy thresholds (mention_count + evidence_count + recency)
export const ZONE_THRESHOLDS = {
  inner: {
    minMentions: 8,
    minEvidence: 5,
    maxDaysSinceLastSeen: 30,
  },
  community: {
    minMentions: 3,
    minEvidence: 2,
    maxDaysSinceLastSeen: 90,
  },
  // Everything else falls to network zone
};

// Badge indicators
export const BADGE_TYPES = {
  growth: { color: '#10b981', icon: 'trending_up' },
  risk: { color: '#ef4444', icon: 'warning' },
  neutral: { color: '#6366f1', icon: 'info' },
};

// Timeline phase defaults
export const TIMELINE_PHASES = [
  { id: 'phase-90', label: '90 Day', flex: 0.5 },
  { id: 'phase-2y1', label: 'Year 2 H1', flex: 1 },
  { id: 'phase-2y2', label: 'Year 2 H2', flex: 1 },
];

// Network stats display
export const STAT_CHIPS = [
  { key: 'totalPeople', label: 'People', icon: 'people' },
  { key: 'totalConnections', label: 'Connections', icon: 'hub' },
  { key: 'avgStrength', label: 'Avg Strength', icon: 'insights' },
];

// Detail panel dimensions
export const DETAIL_PANEL_WIDTH = 420;

// Visualization modes
export const VIZ_MODES = {
  CONCENTRIC: 'concentric',
  FORCE: 'force',
};

// Animation durations (ms)
export const ANIMATION = {
  nodeTransition: 400,
  ringTransition: 600,
  panelSlide: 300,
};

// Empty/loading state messages
export const MESSAGES = {
  loading: 'Loading ego network data...',
  empty: 'No network data yet. Data appears after conversations are processed.',
  error: 'Failed to load network data.',
  noEntity: 'Please select an entity from the header to view the ego network.',
  selectNode: 'Select a person or connection to view details',
  comingSoon: 'Coming soon',
};
